/**
 * Role Type Definitions
 *
 * TypeScript types for role entities and operations.
 * Mirrors backend models from tenant-api/src/models/index.ts
 *
 * Unified Roles Architecture:
 * - Single Role entity with `type` discriminator
 * - Three role types:
 *   - 'tenant': Controls tenant management features (iam, elt, app, etc.)
 *   - 'application': Controls Ketrics resource permissions for applications
 *   - 'app_domain': Defines app-level permissions passed to tenant applications
 * - Both tenant and application roles use ResourceScopedPermission[] format for fine-grained access control
 * - App domain roles define permissions that applications can use for their own authorization
 * - Resource patterns support AWS-style wildcards: *, prefix-*, *-suffix
 * - System roles prefixed with "Ketrics-" are immutable
 */

// ============================================================================
// ROLE TYPE DISCRIMINATOR
// ============================================================================

/**
 * Role type discriminator
 * - tenant: Controls tenant management features (iam, elt, app, etc.)
 * - application: Controls Ketrics resource permissions for applications (volumes, secrets, etc.)
 * - app_domain: Defines app-level permissions passed to tenant applications
 *               (enforcement is app-side, Ketrics just passes the info)
 */
export const RoleType = {
  TENANT: 'tenant',
  APPLICATION: 'application',
  APP_DOMAIN: 'app_domain',
} as const;

export type RoleType = typeof RoleType[keyof typeof RoleType];

// ============================================================================
// PERMISSION TYPES
// ============================================================================

/**
 * Permission action types
 */
export const PermissionAction = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  LIST: 'list',
  EXECUTE: 'execute',
  ALL: '*',
} as const;

export type PermissionAction = typeof PermissionAction[keyof typeof PermissionAction];

/**
 * Resource-scoped permission object
 *
 * Used by both tenant roles and application roles for fine-grained access control.
 * Multiple actions can share the same resource patterns.
 *
 * @example
 * // Tenant role permission - multiple actions on specific applications
 * {
 *   actions: ["application:DescribeApplication", "application:UpdateApplication"],
 *   resources: ["application:inventory", "application:sales-*"],
 *   name: "Application Management",
 *   description: "Read and update access to specific applications"
 * }
 *
 * // Tenant role permission - access to all users
 * { actions: ["iam:GetUser"], resources: ["*"] }
 *
 * // Application role permission - multiple actions on specific volumes
 * {
 *   actions: ["volume:GetObject", "volume:ListObjects"],
 *   resources: ["volume:documents", "volume:exports-*"],
 *   name: "Read-Only Data Access"
 * }
 */
export interface ResourceScopedPermission {
  actions: string[]; // Permission actions (e.g., ["iam:GetUser", "iam:UpdateUser"])
  resources: string[]; // Resource patterns (e.g., ["*"], ["application:inventory", "application:sales-*"])
  name?: string; // Optional human-readable name for this permission group
  description?: string; // Optional description explaining the purpose
}

/**
 * Legacy resource-scoped permission format for backward compatibility during migration
 * @deprecated Use ResourceScopedPermission with actions array instead
 */
export interface LegacyResourceScopedPermission {
  permission: string;
  resources: string[];
}

/**
 * Normalize a permission object to the new format
 * Handles both legacy (permission: string) and new (actions: string[]) formats
 * Preserves optional name and description fields if present
 */
export function normalizePermission(perm: ResourceScopedPermission | LegacyResourceScopedPermission): ResourceScopedPermission {
  if ('actions' in perm && Array.isArray(perm.actions)) {
    return perm as ResourceScopedPermission;
  }
  // Legacy format - convert
  const legacy = perm as LegacyResourceScopedPermission;
  return {
    actions: [legacy.permission],
    resources: legacy.resources,
  };
}

/**
 * Normalize an array of permissions to the new format
 */
export function normalizePermissions(
  permissions: (ResourceScopedPermission | LegacyResourceScopedPermission)[]
): ResourceScopedPermission[] {
  return permissions.map(normalizePermission);
}

/**
 * @deprecated Use ResourceScopedPermission instead
 * Structured permission object (legacy)
 */
export interface Permission {
  resource: string; // Resource identifier (e.g., "inventory", "orders")
  actions: string[]; // Actions allowed (e.g., ["create", "read", "update", "delete"])
}

/**
 * @deprecated Use ResourceScopedPermission instead
 * Permission can be either string format or structured format
 */
export type PermissionValue = string | Permission;

// ============================================================================
// ROLE ASSIGNMENT TYPES
// ============================================================================

/**
 * User assignment with email for display
 */
export interface UserAssignment {
  userId: string;
  email: string; // User's email for display
}

/**
 * Group assignment with code for display
 */
export interface GroupAssignment {
  groupId: string;
  groupCode: string; // Group's unique code for display
}

/**
 * Application assignment with code and name for display
 * Used for bidirectional sync between roles and applications
 */
export interface ApplicationAssignment {
  applicationId: string;
  applicationCode: string; // Application's unique code for display
  applicationName: string; // Application's display name
}

/**
 * Service account assignment with code and name for display
 * Used for bidirectional sync between roles and service accounts
 */
export interface ServiceAccountAssignment {
  serviceAccountId: string;
  serviceAccountCode: string; // Service account's unique code for display
  serviceAccountName: string; // Service account's display name
}

// ============================================================================
// ROLE ENTITY
// ============================================================================

/**
 * Role - Unified role entity for RBAC
 * Three types:
 * - tenant: for tenant management
 * - application: for Ketrics resource access
 * - app_domain: for app-level permissions passed to tenant applications
 */
export interface Role {
  tenantId: string;
  roleId: string; // UUID v4
  roleCode: string; // Unique code (e.g., "admin", "viewer") - immutable after creation
  roleName: string;
  description?: string;

  // Organizational path for grouping roles in a hierarchy (e.g., "/", "/finance", "/finance/reports")
  path: string;

  // Role type discriminator
  type: RoleType;

  // System role flag (only Ketrics-* prefixed roles)
  isSystemRole: boolean;

  // Permissions array using ResourceScopedPermission format
  // Both tenant and application roles use this format for fine-grained access control
  permissions: ResourceScopedPermission[];

  // Direct user assignments
  users: UserAssignment[];

  // Group assignments
  groups: GroupAssignment[];

  // Application assignments (only for APPLICATION-type roles)
  applications?: ApplicationAssignment[];

  // Service account assignments (only for APP_DOMAIN-type roles)
  serviceAccounts?: ServiceAccountAssignment[];

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Role assignment stored on User/Group entities
 */
export interface RoleAssignment {
  roleId: string;
  roleCode: string;
  roleName: string;
  type: RoleType;
}

/**
 * Sanitized role (for API responses)
 */
export type RoleSanitized = Role;

// ============================================================================
// DTOs
// ============================================================================

/**
 * DTO for role creation
 */
export interface RoleCreateDTO {
  roleCode: string; // Required: unique code within tenant
  roleName: string;
  description?: string;
  path?: string;
  type: RoleType; // Required: role type
  permissions: ResourceScopedPermission[];
  users?: UserAssignment[];
  groups?: GroupAssignment[];
}

/**
 * DTO for role updates
 */
export interface RoleUpdateDTO {
  roleName?: string;
  description?: string;
  path?: string;
  permissions?: ResourceScopedPermission[];
  users?: UserAssignment[];
  groups?: GroupAssignment[];
}

/**
 * DTO for role assignment
 * Role type constraints are enforced on the backend:
 * - TENANT roles: Only users and groups
 * - APPLICATION roles: Only applications
 * - APP_DOMAIN roles: Users, groups, and service accounts
 */
export interface RoleAssignDTO {
  users?: string[];
  groups?: string[];
  applicationIds?: string[];
  serviceAccountIds?: string[];
}

// ============================================================================
// QUERY TYPES
// ============================================================================

/**
 * Role list query parameters
 */
export interface RoleListQuery {
  search?: string;
  limit?: number;
}

/**
 * Role list response
 */
export interface RoleListResponse {
  roles: RoleSanitized[];
  count: number;
  lastEvaluatedKey?: Record<string, unknown>;
  applicationCode?: string; // Only present when listing roles by application
}
